import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, entitlementState } from '../utils/api';
import { LockedFeaturePanel } from '../components/EntitlementStates';
import { useAuth } from '../contexts/AuthContext';

function fmtMoney(n) {
  if (!n) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

export default function BuyerMatch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { plan } = useAuth();
  const [deal, setDeal] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [totalScored, setTotalScored] = useState(0);
  const [locked, setLocked] = useState(null);   // buyer_database not on plan (e.g. demo)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const d = await apiGet(`/api/deals/${id}`); if (!cancelled) setDeal(d); } catch { /* leave null */ }
      try {
        const buyersData = await apiGet(`/api/buyers/match/${id}`);
        if (cancelled) return;
        if (Array.isArray(buyersData)) { setBuyers(buyersData); setTotalScored(buyersData.length); }
        else { setBuyers(buyersData.buyers || []); setTotalScored(buyersData.total_scored || 0); }
      } catch (err) {
        const s = entitlementState(err);
        if (!cancelled && s?.kind === 'locked') setLocked(s.error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <LoadingScreen />;
  if (locked) return <div style={{ padding: 24 }}><LockedFeaturePanel feature={locked.feature || 'buyer_database'} plan={locked.plan || plan?.key} /></div>;
  if (!deal) return <div style={{ color: 'var(--gray-500)', padding: 40 }}>Deal not found.</div>;

  const topBuyers = buyers.slice(0, 5);
  const otherBuyers = buyers.slice(5);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button onClick={() => navigate('/pipeline')} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Pipeline
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 4, letterSpacing: '-0.02em' }}>Buyer Match</h1>
            <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>
              {deal.address} · {deal.neighborhood} · {deal.beds}bd/{deal.baths}ba
            </div>
          </div>
          <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: 'var(--gray-300)' }}>
            <span style={{ color: 'var(--teal)', fontWeight: 700 }}>{totalScored.toLocaleString()}</span> buyers scored · showing top <span style={{ color: 'var(--teal)', fontWeight: 700 }}>{buyers.length}</span>
          </div>
        </div>
      </div>

      {/* Deal summary card */}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '16px 24px', marginBottom: 24, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
        {[
          { label: 'Max Offer', value: fmtMoney(deal.max_offer), color: 'var(--teal)' },
          { label: 'Est. ARV', value: fmtMoney(deal.ai_arv), color: 'var(--white)' },
          { label: 'Repair Cost', value: fmtMoney(deal.repair_cost), color: 'var(--gray-300)' },
          { label: 'AI Score', value: deal.ai_deal_score, color: deal.ai_deal_score >= 85 ? '#4A7C59' : '#E0A526' },
          { label: 'Stage', value: deal.stage, color: 'var(--gray-300)' },
        ].map(({ label, value, color }) => (
          <div key={label}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Top Matches */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>Top Buyer Matches</h2>
        <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20 }}>Ranked by AI compatibility score across neighborhood, price range, strategy, and reliability</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
          {topBuyers.map((buyer, idx) => (
            <BuyerCard key={buyer.id} buyer={buyer} rank={idx + 1} dealId={id} navigate={navigate} />
          ))}
        </div>
      </div>

      {/* Other Buyers */}
      {otherBuyers.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 16 }}>Other Buyers in Network</h3>
          <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--navy-border)' }}>
                  {['Buyer', 'Strategy', 'Neighborhoods', 'Max Price', 'Purchases', 'Match'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(26, 23, 20, 0.025)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {otherBuyers.map((buyer, idx) => (
                  <tr key={buyer.id} style={{ borderBottom: idx < otherBuyers.length - 1 ? '1px solid rgba(232, 227, 218, 0.7)' : 'none' }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(26, 23, 20, 0.025)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{buyer.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{buyer.company}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-300)' }}>{buyer.buy_box_strategies}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)', maxWidth: 180 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buyer.buy_box_neighborhoods}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-300)', fontWeight: 500 }}>{fmtMoney(buyer.buy_box_max_price)}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-300)' }}>{buyer.past_purchases}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <ScoreBar score={buyer.match_score} small />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BuyerCard({ buyer, rank, dealId, navigate }) {
  const [expanded, setExpanded] = useState(rank === 1);
  const scoreColor = buyer.match_score >= 80 ? '#4A7C59' : buyer.match_score >= 60 ? '#E0A526' : '#B85450';
  const rankColors = ['var(--gold)', '#C0C0C0', '#CD7F32'];

  return (
    <div style={{
      background: 'var(--navy-card)',
      border: `1px solid ${rank === 1 ? 'rgba(224, 165, 38,0.3)' : 'var(--navy-border)'}`,
      borderRadius: 12, overflow: 'hidden',
      boxShadow: rank === 1 ? '0 4px 24px rgba(224, 165, 38,0.08)' : 'none',
    }}>
      <div
        style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Rank */}
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: rank <= 3 ? `${rankColors[rank - 1]}22` : 'var(--navy-border)',
          border: rank <= 3 ? `2px solid ${rankColors[rank - 1]}` : '2px solid transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, color: rank <= 3 ? rankColors[rank - 1] : 'var(--gray-500)',
        }}>
          {rank}
        </div>

        {/* Buyer info */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', marginBottom: 2 }}>{buyer.name}</div>
          <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 500, marginBottom: 2 }}>{buyer.company}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            {buyer.past_purchases} purchases · {fmtMoney(buyer.total_volume)} volume · avg {buyer.avg_close_days}d close
          </div>
        </div>

        {/* Match reasons */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 200 }}>
          {buyer.match_reasons?.map(r => (
            <span key={r} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 12, background: 'rgba(20, 80, 74,0.1)', color: 'var(--teal)', fontWeight: 500 }}>
              {r}
            </span>
          ))}
        </div>

        {/* Match score */}
        <div style={{ textAlign: 'center', minWidth: 100, flexShrink: 0 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{buyer.match_score}%</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>match</div>
          <div style={{ height: 4, background: 'var(--navy-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${buyer.match_score}%`, height: '100%', background: scoreColor, borderRadius: 2, transition: 'width 0.8s ease' }} />
          </div>
        </div>

        {/* Closing speed */}
        <div style={{ textAlign: 'right', minWidth: 90, flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 2 }}>Close speed</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: buyer.avg_close_days <= 20 ? '#4A7C59' : buyer.avg_close_days <= 30 ? '#E0A526' : '#B85450' }}>
            {buyer.closing_speed}
          </div>
        </div>

        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" strokeWidth="2" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--navy-border)', padding: '16px 20px', background: 'rgba(26, 23, 20, 0.02)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Buy Box</div>
              <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6 }}>
                <div>Min beds: {buyer.buy_box_min_beds}+</div>
                <div>Max price: {fmtMoney(buyer.buy_box_max_price)}</div>
                <div>Sqft: {buyer.buy_box_min_sqft?.toLocaleString()}–{buyer.buy_box_max_sqft?.toLocaleString()}</div>
                <div style={{ marginTop: 4, color: 'var(--teal)', fontWeight: 500 }}>{buyer.buy_box_strategies}</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Target Neighborhoods</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {buyer.buy_box_neighborhoods?.split(',').map(n => (
                  <span key={n} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: 'var(--navy-border)', color: 'var(--gray-300)' }}>
                    {n.trim()}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Financials</div>
              <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6 }}>
                <div>Proof of funds: {fmtMoney(buyer.proof_of_funds)}</div>
                <div>Avg close: {buyer.avg_close_days} days</div>
                <div>Total volume: {fmtMoney(buyer.total_volume)}</div>
              </div>
            </div>
          </div>
          {buyer.score_breakdown && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Score Breakdown</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'Location', pts: buyer.score_breakdown.location, max: 40 },
                  { label: 'Lead Quality', pts: buyer.score_breakdown.quality, max: 25 },
                  { label: 'Strategy', pts: buyer.score_breakdown.strategy, max: 20 },
                  { label: 'Price Range', pts: buyer.score_breakdown.price, max: 15 },
                ].map(({ label, pts, max }) => (
                  <div key={label} style={{ flex: '1 1 120px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{label}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: pts === max ? '#4A7C59' : pts > 0 ? 'var(--gray-300)' : 'var(--gray-500)' }}>{pts}/{max}</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--navy-border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${(pts / max) * 100}%`, height: '100%', background: pts === max ? '#4A7C59' : pts > 0 ? 'var(--teal)' : 'transparent', borderRadius: 2 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {buyer.notes && (
            <div style={{ padding: '10px 14px', background: 'var(--navy)', borderRadius: 8, fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6, marginBottom: 12 }}>
              <span style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Notes: </span>{buyer.notes}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={ctaBtnStyle('var(--teal)')}>Send Deal Packet</button>
            <button style={ctaBtnStyle('var(--gold)')}>Start Follow-Up Sequence</button>
            <a href={`mailto:${buyer.email}`} style={{ ...ctaBtnStyle('var(--gray-300)'), textDecoration: 'none', display: 'inline-block' }}>Email {buyer.email}</a>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score, small }) {
  const color = score >= 80 ? '#4A7C59' : score >= 60 ? '#E0A526' : '#B85450';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: small ? 40 : 60, height: 5, background: 'var(--navy-border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color }}>{score}%</span>
    </div>
  );
}

function ctaBtnStyle(color) {
  return {
    padding: '7px 14px', borderRadius: 7, border: `1px solid ${color}40`,
    background: `${color}15`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  };
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '3px solid var(--navy-border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: 'var(--gray-500)', fontSize: 14 }}>Matching buyers to deal...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
